import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { ClassroomFacade } from 'shared/classroom/data-access';

/**
 * Signal-based UI state for the toolbar. Currently owns the
 * `goToParentInProcess` flag — set when the user clicks the
 * "navigate to parent lection/chapter" toolbar button (or when the
 * SignalR equivalent is received), reset when a new slide is shown on
 * any layer or when the navigation-preview basic-slides list changes
 * in Y.Doc (signals that navigation completed).
 *
 * Stage D.6 (write-path refactor) moved this flag out of the toolbar
 * NgRx reducer. The reducer-based version listened to four actions:
 *   - `ToolbarActions.toolbarNavigateToParent` → set true
 *   - `InternalClassroomActions.show*SlideInPreviewScreen` (×3) → set false
 *   - `classroomConnectionActions.setNavigationPreviewBasicSlides` → set false
 *
 * The three `show*` actions are state-mirrors of the Y.Doc layer-slide
 * Signals (Q1 audit's DELETE-SIGNAL class) and the basic-slides action
 * is dispatched from `classroom-consumer.effects.ts:onYjsBasicSlidesChanged$`
 * when the Y.Doc field changes. Both reset triggers are observable
 * directly via the Y.Doc Signals on `ClassroomFacade`; this
 * service collapses the action-bus indirection.
 */
@Injectable({ providedIn: 'root' })
export class ToolbarUiStateService {
    private readonly lessonState = inject(ClassroomFacade);

    private readonly goToParentInProcessSignal = signal(false);

    readonly goToParentInProcess: Signal<boolean> = this.goToParentInProcessSignal;

    /**
     * Aggregate Signal that changes whenever any layer's slide changes OR the
     * navigation-preview basic-slides Y.Doc field changes. Used by the reset
     * effect below.
     */
    private readonly resetTrigger = computed(() => ({
        primary: this.lessonState.primarySlide(),
        secondary: this.lessonState.secondarySlide(),
        tertiary: this.lessonState.tertiarySlide(),
        navigationPreviewBasicSlides:
            this.lessonState.state()?.navigationPreviewBasicSlides,
    }));

    constructor() {
        // Reset the flag on any of the trigger signals' value change.
        // `untracked` around the setter avoids re-entering the effect.
        let initialized = false;
        effect(() => {
            this.resetTrigger(); // subscribe to the aggregate
            if (!initialized) {
                initialized = true;
                return;
            }
            untracked(() => this.goToParentInProcessSignal.set(false));
        });
    }

    /**
     * User clicked the "navigate to parent" toolbar button (or the SignalR
     * equivalent was received and dispatched as the toolbar action). Marks
     * the navigation as in-progress so the button disables until the
     * resulting state change lands in Y.Doc.
     */
    markGoToParentClicked(): void {
        this.goToParentInProcessSignal.set(true);
    }
}
